import $ from 'jquery'
import { isEqual } from 'lodash'
import get from 'lodash/get'
import has from 'lodash/has'
import includes from 'lodash/includes'
import set from 'lodash/set'
import * as OLColor from 'ol/color'
import OLLayerGroup from 'ol/layer/Group'
import OLStyleCircle from 'ol/style/Circle'
import OLStyleFill from 'ol/style/Fill'
import OLStyleIcon from 'ol/style/Icon'
import OLStyleRegularShape from 'ol/style/RegularShape'
import OLStyleStroke from 'ol/style/Stroke'
import OLStyleStyle from 'ol/style/Style'

import type { OLLayerWMS } from './MapLayerWMS'
import type OLLayer from 'ol/layer/Layer'
import type OLLayerVector from 'ol/layer/Vector'
import type OLLayerVectorTile from 'ol/layer/VectorTile'

import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import type { ConfigLayerOptions } from 'config/ConfigLayer'
import type { ConfigLayerCircleStyleOptions } from 'config/ConfigLayerCircleStyleOptions'
import type { ConfigLayerRegularShapeOptions } from 'config/ConfigLayerRegularShapeOptions'
import type { MapLayerGeoJsonLegacyStyleOptions } from 'config/MapLayerGeoJson'
import SetAttributions from 'config/SetAttributions'
import { addLayers } from 'drawer/tabs/layers/actions'
import { SELECTOR_TYPE_CARET, SELECTOR_TYPE_CHECKBOX, SELECTOR_TYPE_RADIO } from 'drawer/tabs/layers/Layers'
import IsExclusiveGroup from 'drawer/tabs/layers/utils/IsExclusiveGroup'
import ModelUpdateLayer from 'drawer/tabs/layers/utils/ModelUpdateLayer' // Update Legacy Model
import PoziEvents from 'utils/events'
import PoziNotifications from 'utils/notifications'

export class MapLayer {
  // Enrich layerConf with extra values
  // Note: this is a mixed bag of stuff. Some of which may not be needed.
  // TODO: go through this and determine what is actually necessary.
  protected static preLayer (layerConf: ConfigLayerOptions) {
    if (!layerConf || typeof layerConf === 'string') {
      console.error('preLayer - config must be an object.', layerConf)
      return
    }

    if (!layerConf.type) {
      console.error('PreLayer - config does not have a type.', layerConf)
      return
    }

    const layerTypeLC: string = layerConf.type.toLowerCase()

    if (layerTypeLC === 'search') {
      // console.log('Layer is for search only. Not an openlayers config object')
      return
    }

    // Set crossOrigin flag to any layer that needs it
    // TODO: merge the 2 settings
    // Maybe move this to other section where layerConf updates get set?
    if (!layerConf.crossOrigin) {
      layerConf.crossOrigin = 'Anonymous'
    }
    if (!layerConf?.config?.crossOrigin) {
      set(layerConf, 'config.crossOrigin', 'Anonymous')
    }

    // The visible flag is manipulated in the code, but the initial value is used in history.js to determine if it should be shown in the url.
    set(layerConf, 'selectedAtLoad', layerConf.visible)

    let url = get(layerConf, 'config.spatial.url', '')
    if (url !== '' && includes(url, '[timestamp]')) {
      url = url.replace('[timestamp]', Date.now())
      set(layerConf, 'config.spatial.url', url)
    }

    if (has(layerConf, 'config.params.nocache')) {
      set(layerConf, 'config.params.nocache', Date.now())
    }

    // This may not be needed anymore since PoziServer by default uses EPSG:4326
    // TODO: Check and maybe remove this block.
    if (layerConf.localDataSource) {
      const sourceParam = get(layerConf, 'config.spatial.params.source', null)
      const optionsParam = get(layerConf, 'config.spatial.params.options', null)
      if (sourceParam && !optionsParam) {
        set(layerConf, 'config.spatial.params.options', '-t_srs|EPSG:4326')
      }
    }

    // Mark layer as selected if it is visible on start-up
    // This will make sure that basemap layers show as selected
    // Explicitly set seleected as boolean in case the visibility flag was undefined
    set(layerConf, 'selected', !!layerConf.visible)

    const layerIsCatalogue = PoziUtils.isLayerTypeCatalogue(layerConf)
    if (IsExclusiveGroup(layerConf.groupid)) { // basemap
      // @ts-expect-error
      layerConf.selectorType = SELECTOR_TYPE_RADIO
    } else if (layerIsCatalogue || layerConf.type.toLowerCase() === 'folder') { // layer tree
      // @ts-expect-error
      layerConf.selectorType = SELECTOR_TYPE_CARET
    } else {
      // @ts-expect-error
      layerConf.selectorType = SELECTOR_TYPE_CHECKBOX
    }

    // Prevent queries/clicks and (therefore) tooltips when a layer belongs
    // to any of the following groups
    const preventQueryGroups = ['basemap', 'imagery']
    if (preventQueryGroups.includes(layerConf.groupid)) {
      layerConf.queryable = false
    }

    // TODO: Check if this is still necessary?
    set(layerConf, 'drawer.added', true)
    set(layerConf, 'drawer.visible', !layerConf.showInLayerControl)

    // Convert any layer profiles from string to array
    if (typeof layerConf.profiles === 'string') {
      layerConf.profiles = layerConf.profiles
        .split(';')
        .map(s => s.trim())
        .filter(s => !!s)
    }
  }

  static setVisible (layerConf: ConfigLayerOptions, olLayer: OLLayerGroup |
    OLLayerWMS |
    OLLayerVectorTile |
    OLLayerVector |
    undefined) {
    //
    // Set attribution text/html for basemap layers
    SetAttributions(olLayer)

    // requires layerConf to be stored in Model.get('config.layers')
    PoziEvents().onLayerVisible(olLayer, PoziUtils.isLayerSelected(layerConf))
  }

  static getStyle (layerConf: ConfigLayerOptions, olLayer?: OLLayerGroup |
    OLLayerWMS |
    OLLayerVectorTile |
    OLLayerVector |
    undefined) {
    // legacyStyleOptions is a bit of a hack to get styles for GeoJSON layers
    // TODO: clean this up at some stage
    const legacyStyleOptions: MapLayerGeoJsonLegacyStyleOptions = olLayer ? olLayer.get('legacyStyleOptions') : undefined
    const highlightImageSrc = 'img/bubble-highlight/png-100x100/bubble-highlight-FFA07A.png'

    // A selection style: the regular style + a bubble highlight
    let highlight = new OLStyleStyle({
      image: new OLStyleIcon({
        crossOrigin: 'Anonymous',
        size: [100, 100],
        scale: 0.6,
        anchor: [50, 82],
        anchorXUnits: 'pixels',
        anchorYUnits: 'pixels',
        opacity: 0.75,
        src: highlightImageSrc
      }),
      zIndex: 52
    })
    // A default selection style: the regular style + a red dot
    const color = OLColor.fromString('rgba(255, 0, 0, 0.5)')
    const redCircleStyleOptions: ConfigLayerCircleStyleOptions = {
      crossOrigin: 'Anonymous',
      radius: 10,
      fill: new OLStyleFill({
        color
      }),
      stroke: new OLStyleStroke({
        color: 'red',
        width: 1
      })
    }
    if (layerConf.style && layerConf.style.noBubbleContainer) {
      highlight = new OLStyleStyle({
        image: new OLStyleCircle(redCircleStyleOptions),
        zIndex: 2
      })
    }

    let pointSelectedStyle
    if (layerConf.style) {
      const styleNotIcon = layerConf.style.polygon || layerConf.style.linestring || layerConf.style.point
      if (styleNotIcon) {
        let highlightStyleForGeometryType: OLStyleStyle
        if (styleNotIcon.radius) {
          if (styleNotIcon.points) {
            // Regular shape style
            const regularShapeOptions: ConfigLayerRegularShapeOptions = {
              crossOrigin: 'Anonymous',
              radius: styleNotIcon.radius || 1,
              stroke: new OLStyleStroke({
                color: 'orange',
                width: 6
              }),
              fill: new OLStyleFill({
                color: 'rgba(255, 0, 0, 0.1)'
              }),
              points: styleNotIcon.points,
              angle: styleNotIcon.angle || Math.PI / 4
            }
            highlightStyleForGeometryType = new OLStyleStyle({
              image: new OLStyleRegularShape(regularShapeOptions)
            })
          } else {
            // Circle style
            const orangeCircleStyleOptions: ConfigLayerCircleStyleOptions = {
              crossOrigin: 'Anonymous',
              radius: styleNotIcon.radius,
              stroke: new OLStyleStroke({
                color: 'orange',
                width: 2
              }),
              fill: new OLStyleFill({
                color: 'rgba(255, 0, 0, 0.1)'
              })
            }
            highlightStyleForGeometryType = new OLStyleStyle({
              image: new OLStyleCircle(orangeCircleStyleOptions)
            })
          }
        } else {
          let stroke = {
            color: 'orange',
            width: 6
          }
          let fill = {
            color: 'rgba(255, 0, 0, 0.1)'
          }

          let typePolygon
          let typeLinestring

          if (layerConf.styleSelected) {
            typePolygon = has(layerConf, 'styleSelected.polygon')
            typeLinestring = has(layerConf, 'styleSelected.linestring')

            if (typePolygon) {
              stroke = get(layerConf, 'styleSelected.polygon.stroke', stroke)
              fill = get(layerConf, 'styleSelected.polygon.fill', fill)
            }
            if (typeLinestring) {
              stroke = get(layerConf, 'styleSelected.linestring.stroke', stroke)
              fill = get(layerConf, 'styleSelected.linestring.fill', fill)
            }
          } else {
            typePolygon = has(layerConf, 'style.polygon')
            typeLinestring = has(layerConf, 'style.linestring')

            if (typePolygon) {
              if (has(layerConf, 'style.polygon.stroke')) {
                stroke = $.extend({}, layerConf.style.polygon?.stroke)
                stroke.color = 'orange'
                stroke.width = 6
              }
              if (has(layerConf, 'style.polygon.fill')) {
                fill = $.extend({}, layerConf.style.polygon?.fill)
              }
            }

            if (typeLinestring) {
              if (has(layerConf, 'style.linestring.stroke')) {
                stroke = $.extend({}, layerConf.style.linestring?.stroke)
                stroke.color = 'orange'
              }
              if (has(layerConf, 'style.linestring.fill')) {
                fill = $.extend({}, layerConf.style.linestring?.fill)
              }
            }
          }
          highlightStyleForGeometryType = new OLStyleStyle({
            stroke: new OLStyleStroke(stroke),
            fill: new OLStyleFill(fill)
          })
        }
        pointSelectedStyle = function () {
          return [highlightStyleForGeometryType]
        }
      } else {
        if (!has(legacyStyleOptions, 'pointSelectedStyle')) {
          pointSelectedStyle = function () {
            const { backgroundBubble2, shadow, style2 } = legacyStyleOptions || {}

            return [shadow, backgroundBubble2, style2, highlight]
          }
        }
      }
    }

    if (layerConf.styleClass) {
      pointSelectedStyle = function (feature: any) {
        const featureKey: string = PoziUtils.getFeatureKey(feature, layerConf.styleClass?.property || '')
        if (featureKey) {
          // @ts-ignore
          if (legacyStyleOptions?.style2 && legacyStyleOptions?.style2[featureKey]) { // FIXME: I don't know how this index is being used?!
            // @ts-ignore
            return [legacyStyleOptions?.shadow, legacyStyleOptions?.backgroundBubble2[featureKey], legacyStyleOptions?.style2[featureKey], highlight]
          }
        }
      }
    }

    if (legacyStyleOptions && pointSelectedStyle) { // FIXME: configObj is possibly undefined
      legacyStyleOptions.pointSelectedStyle = pointSelectedStyle
    }

    // Keep a pointer to the layer and its initial config
    // This is used in map.js to set the style
    // https://openlayers.org/en/latest/apidoc/ol.Feature.html#setStyle
    let style = {}
    if (legacyStyleOptions && legacyStyleOptions.pointStyle && legacyStyleOptions.pointSelectedStyle) { // FIXME: configObj is possibly undefined
      style = {
        default: function (feature: any, res: any) {
          if (legacyStyleOptions && legacyStyleOptions.pointStyle) {
            return legacyStyleOptions.pointStyle(feature, res)
          }
        },
        selected: function (feature: any, res: any) {
          if (legacyStyleOptions && legacyStyleOptions.pointSelectedStyle) {
            return legacyStyleOptions.pointSelectedStyle(feature, res)
          }
        }
      }
    }
    return style
  }

  static registerLayer (layerConf: ConfigLayerOptions, olLayer: OLLayerGroup |
    OLLayerWMS |
    OLLayerVectorTile |
    OLLayerVector | any |
    undefined, reduxLayerConfs: Set<ConfigLayerOptions> = new Set(), addToMap = true) {
    // Both the layerConf and olLayer are required
    if (!layerConf) {
      console.log('registerLayer: layer conf not provided', layerConf)
      return
    }

    if (!olLayer) {
      console.log('registerLayer: OL layer not provided', layerConf.titleid, layerConf)
      return
    }

    const titleid = layerConf.titleid

    // console.debug('registerLayer', titleid)

    // Register in `config.layers`
    // This store has multiple entrypoints. TODO: unify them
    //
    // Entry points:
    // * Model.get('config.layers')
    // * window.appObject.poziConfig.rawSiteConfig.layers
    // * window.appObject.siteConfig.layers

    const configLayers = Model.get('config.layers')

    // We'll ignore layer folders, i.e. layers that have layers as children. These types
    // of layers are not affected by having duplicate names.
    const configLayersDuplicate = configLayers.find((layerConf: ConfigLayerOptions) => layerConf.titleid === titleid && !layerConf.layers)

    if (!configLayersDuplicate) {
      configLayers.push(layerConf)
      // console.debug('registerLayer - adding layer to config.layers:', titleid)
    } else {
      const duplicateLayerIdentical = isEqual(configLayersDuplicate, layerConf)
      if (duplicateLayerIdentical) {
        // console.log('registerLayer - duplicate layer in config.layers. Not adding again.', titleid, configLayersDuplicate, layerConf)
      } else {
        console.log('registerLayer - duplicate layer in config.layers. Different!', titleid, configLayersDuplicate, layerConf)

        let message = `Duplicate layer name detected: '${layerConf.title}'`

        const groupIdentical = configLayersDuplicate.group === layerConf.group
        if (!groupIdentical && configLayersDuplicate.group && layerConf.group) {
          message += ` found in '${configLayersDuplicate.group}' and '${layerConf.group}'`
        }

        // The following code can be helpful with debugging, therefore we keep it.
        //
        // const typeIdentical = configLayersDuplicate.type === layerConf.type
        // if (!typeIdentical) {
        //   const duplicateIsCatalogue = PoziUtils.isLayerTypeCatalogue(configLayersDuplicate)
        //   const layerIsGroupless = !layerConf.group
        //   if (duplicateIsCatalogue && layerIsGroupless) {
        //     message += '. Catalogue contains layer with identical name which is not supported.'
        //   } else {
        //     message += `. Type: '${configLayersDuplicate.type}' vs '${layerConf.type}'`
        //   }
        // }
        //
        // const layerPathIdentical = isEqual(configLayersDuplicate.layerPathArray, layerConf.layerPathArray)
        // if (!layerPathIdentical) {
        //   message += `. Layer paths differ: '${configLayersDuplicate.layerPathArray?.join('/')}' vs '${layerConf.layerPathArray?.join('/')}'`
        // }

        PoziNotifications().warning(message)
      }
    }

    // Legacy styles
    const style = MapLayer.getStyle(layerConf, olLayer)

    // Register in 'layerConf'
    MapLayer.registerInLayerConfig(layerConf, olLayer, style)

    // Store updated layerConf in mode ('config.layers')
    ModelUpdateLayer(layerConf)

    // Add layer to OL map, but only top level layers as the child layers or OLLayerGroup
    // layers will be added by OL automatically when the parent gets added
    if (addToMap) {
      MapLayer.registerInLayersOL3(olLayer)
    }

    // setVisible requires layerConf to be stored in Model.get('config.layers')
    MapLayer.setVisible(layerConf, olLayer)

    // redux layerConfs: only add layerConfs for data sources that have an OL layer
    if (olLayer) {
      reduxLayerConfs.add(layerConf)
    }

    // Recurse when we have nested layers (usually WXSGetCapabilties/QGISProjectSettings)
    if (olLayer instanceof OLLayerGroup) {
      // const layersArray = olLayer.getLayersArray()
      const layersArray = PoziUtils.getAllOLLayersAndGroups(olLayer) || []

      // We need to reverse the array to add the layers in the correct order
      layersArray.reverse()

      for (const childLayer of layersArray) {
        const childTitleid = childLayer.get('titleid')

        // TODO: find a way to do this without needing to store the layerConf in the OL layer
        const childLayerConf = childLayer.get('layerConf')
        if (!childLayerConf) {
          console.log('registerLayer- Childlayer has no layerConf. Ignoring', childTitleid)
          continue
        }

        // We will use this later to batch-register the child layers with redux
        reduxLayerConfs.add(childLayerConf)

        // console.debug('registerLayer - registering child layer', childTitleid)

        // Child layers do not need to get registered in layersOL as they'll be added to the map
        // automatically by OpenLayers when the group gets added to the map
        const addToLayersOL3 = false

        MapLayer.registerLayer(childLayerConf, childLayer, reduxLayerConfs, addToLayersOL3)
      }
    }
  }

  static registerInLayerConfig (layerConf: ConfigLayerOptions, olLayer: OLLayerGroup |
      OLLayerWMS |
      OLLayerVectorTile |
      OLLayerVector,
  style: any) {
    const app = PoziUtils.getApp()
    const title = layerConf.title
    const titleExists = Object.keys(app.layerConfig).includes(title)
    // console.debug('registerInLayerConfig - addLayer', title, titleExists)

    if (titleExists) {
      // console.log('registerInLayerConfig - addLayer title already exists (ignoring):', layerConf.titleid, titleExists, layerConf)
      return
    }

    app.layerConfig[title] = {
      layer: olLayer,
      config: layerConf,
      initiallyVisible: olLayer ? olLayer.getVisible() : undefined,
      style
    }
  }

  static registerInLayersOL3 (olLayer: OLLayerGroup |
    OLLayerWMS |
    OLLayerVectorTile |
    OLLayerVector) {
    // Directly reference legacy OL layers storage
    const layersOL3 = window.appObject.siteConfig.layersOL3

    // Only add layer if there isn't already one with the same titleid
    // Pozi cannot handle duplicate titleid fields
    //
    // Note: this is a hack. We need to figure out where these duplicate layers
    //       get generated
    const titleid = olLayer.get('titleid')
    const titleidExists = layersOL3.some((layer: OLLayer) => layer.get('titleid') === titleid)

    // console.debug('registerInLayersOL3 - addLayer', titleid, titleidExists, olLayer)

    if (titleidExists) {
      console.log('registerInLayersOL3 - addLayer title already exists (ignoring):', titleid, titleidExists, olLayer)
      return
    }

    // Pushing this layer into the OL3-ready layer array
    layersOL3.push(olLayer)
  }

  static registerInRedux (layerConfs: ConfigLayerOptions[]) {
    // Bundle multiple dispatch calls into one
    window.store.dispatch(addLayers(layerConfs))
  }
}

// For debugging:
//
// * Get all OL registered layers from the map (exluding layergroups):
//   appObject.map.getAllLayers().map(l => l.get('titleid'))
//
// * Get all layers in layerConfig:
//   Object.keys(appObject.layerConfig)
//
// * Get all layers stored in siteConfig:
//   appObject.siteConfig.layers.map(l => l.titleid)
//
// * Get all layersOL3 stored in siteConfig:
//   appObject.siteConfig.layersOL3.map(l => l.get('titleid'))
//
// * Get all layers from redux store
//   Object.keys(window.store.getState().layers)
